import { createSelector } from "@reduxjs/toolkit";
import { IRoomSummary, IRoomsSummaryHashmap } from "@backend/src/shared/schemas/schemas";
import { IRootState } from "../app/store";
import { ROOM_COLORINGS } from "../RoomColoring/roomColorings";
import { IRoomColoring } from "../RoomColoring/roomColoringTypes";
import { selectRoomsWhereDevicesIsNotPresentInSelectedRooms } from "../DesignTab/devices/deviceSelectors";
import {
  selectStandardRoomValidationColorItems,
  selectValidationDecisionColorItems,
  DECISION_FILTER_PREFIX,
  selectSpecifiedArticlesValidationColorItems,
} from "./rfpValidationSelectors";
import {
  selectHttpGetRoomsCurrentFloorResult,
  selectHttpRoomsBasedOnActiveProjectTab,
  selectHttpSingleRoomFull,
} from "./httpRoomSelectors";
import { selectActiveSingleRoomSummary } from "./roomMcbimSharedSelectors";

// ----- Grundselectorer -----

export const selectProjectNo = (state: IRootState) => state.design.projectNo;
const selectedRoomNr = (state: IRootState) => state.design.selectedSpaceNumber;


// ----- Växla datakälla beroende på aktiv flik -----

export const selectActiveProjectTab = (state: IRootState) => state.global.activeProjectTab;



export const selectActiveRoom = createSelector(
  [selectedRoomNr, (state: IRootState, rooms: IRoomSummary[]) => rooms],
  (roomNr, rooms): IRoomSummary | undefined => {
    if (!roomNr) return undefined;
    return rooms.find((room) => room.roomNumber === roomNr);
  }
);

// Kombinerar statiska färgfilter med dynamiska standardrom-filter + decision-filter
export const selectRoomColorings = createSelector(
  [
    () => ROOM_COLORINGS, // stabil referens till dina statiska filter
    selectStandardRoomValidationColorItems, // dynamiskt byggda standardRoom-filter (matchRate etc)
    selectValidationDecisionColorItems, // ✅ dynamiskt byggda decision-status-filter (current.status)
    selectSpecifiedArticlesValidationColorItems,
  ],
  (
    staticFilters,
    standardRoomFilters,
    decisionFilters,
    specifiedArticlesFilters
  ): IRoomColoring[] => {
    // undvik dubletter på id; statiska prioriteras
    const existing = new Set(staticFilters.map((f) => f.id));

    const onlyNewStandard = standardRoomFilters.filter((f) => !existing.has(f.id));
    onlyNewStandard.forEach((f) => existing.add(f.id));

    const onlyNewDecisions = decisionFilters.filter((f) => !existing.has(f.id));

    return [...staticFilters, ...onlyNewStandard, ...onlyNewDecisions, ...specifiedArticlesFilters];
  }
);

export const selectActiveRoomColoringId = (state: IRootState) =>
  state.design.activeRoomColoringId;

export const selectActiveRoomColoring = createSelector(
  [selectRoomColorings, selectActiveRoomColoringId],
  (roomColorings, activeRoomColoringId) => {
    if (!activeRoomColoringId) return null;
    return roomColorings.find((roomColoring) => roomColoring.id === activeRoomColoringId) ?? null;
  }
);

export const selectStandardRoomDisciplineFromRoomColoringId = createSelector(
  [selectActiveRoomColoringId],
  (roomColoringId): string | undefined => {
    const discipline = roomColoringId?.match(/^standardRoom\.(.)/)?.[1];
    return discipline ?? undefined;
  }
);

export const selectAvailableStandardRoomValidationDisciplines = createSelector(
  [selectHttpSingleRoomFull],
  (room) => {
    const disciplines: string[] = [];
    const standardRoomValidation = room?.validation?.standardRoom;
    if (standardRoomValidation && typeof standardRoomValidation === "object") {
      Object.keys(standardRoomValidation).forEach((key) => {
        disciplines.push(key as string);
      });
    }
    return disciplines;
  }
);

export const selectStandardRoomValidationInfoInSelectedRoom = createSelector(
  [selectHttpSingleRoomFull, selectStandardRoomDisciplineFromRoomColoringId],
  (room, disciplineTab) => {
    if (!room || !disciplineTab) return null;
    const standardRoomValidation = room.validation?.standardRoom;
    if (!standardRoomValidation) return null;
    return standardRoomValidation[disciplineTab] ?? null;
  }
);
export const selectMlDevicesAvailableInSelectedSpace = createSelector(
  [selectHttpSingleRoomFull],
  (singleRoom) => {
    if (!singleRoom) {
      return false;
    }
    const count = singleRoom.mlPredictedDevices?.length ?? 0;
    return count > 0;
  }
);

export const selectActiveRoomHasStandardRoom = createSelector(
  [selectActiveSingleRoomSummary],
  (room) => {
    if (!room) {
      return false;
    }

    return room.rfpMalRom != null && room.rfpMalRom !== "";
  }
);

export const selectActiveRoomIsLocked = createSelector([selectHttpSingleRoomFull], (room) => {
  if (!room) {
    return false;
  }
  return room.multiplacerCeilingDevicesLocked?.bool === true;
});

const getStandardRoomDisciplineFromRoomColoringId = (
  activeId: string | undefined
): string | undefined => {
  if (!activeId) return undefined;

  // "decision:standardRoom.E" -> "standardRoom.E"
  const withoutPrefix = activeId.startsWith(DECISION_FILTER_PREFIX)
    ? activeId.slice(DECISION_FILTER_PREFIX.length)
    : activeId;

  // Vi bryr oss bara om standardRoom.*
  if (!withoutPrefix.startsWith("standardRoom.")) return undefined;

  const parts = withoutPrefix.split(".");
  return parts[1] ?? null; // "E"
};

export const selectStandardRoomDisciplineFromActiveRoomColoring = createSelector(
  [selectActiveRoomColoringId],
  (activeId) => getStandardRoomDisciplineFromRoomColoringId(activeId)
);

export const selectRoomNumbers = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsMap): string[] => {
    return Object.values(roomsMap).map((room) => room.roomNumber.toString());
  }
);

export const selectedRooms = (state: IRootState) => state.design.selectedRooms;

export const selectRoomsWithRfpMalRoomInSelectedRooms = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab, selectedRooms],
  (roomsMap, selectedRooms): IRoomSummary[] => {
    const rooms: IRoomSummary[] = [];

    selectedRooms.forEach((roomNr) => {
      const room = roomsMap[roomNr];
      if (room && room.rfpMalRom) {
        rooms.push(room);
      }
    });

    return rooms;
  }
);

/**
 * ✅ NY: Rom vi kan plassere komponenter i, basert på reglene:
 * - Rommet er valgt
 * - Rommet har INGEN enheter
 * - Rommet har rfpMalRom (standardrom)
 *
 * Returnerer roomNumbers + counts så modalen kan skrive:
 * "Av X valgte rom, finnes Y uten enheter, av disse Y er Z med standardrom"
 */
export const selectStandardRoomPlacementStatsInSelectedRooms = createSelector(
  [
    selectedRooms,
    selectRoomsWhereDevicesIsNotPresentInSelectedRooms,
    selectRoomsWithRfpMalRoomInSelectedRooms,
  ],
  (selected, roomsWithoutDevices, roomsWithStandardRoom) => {
    const selectedCount = selected?.length ?? 0;

    // roomsWithStandardRoom er basert på selectedRooms (uavhengig av devices)
    const roomsWithStandardRoomSet = new Set(roomsWithStandardRoom.map((r) => r.roomNumber));

    // De vi faktisk kan plassere i = (uten enheter) ∩ (har standardrom)
    const placeableRoomNumbers = roomsWithoutDevices.filter((roomNo) =>
      roomsWithStandardRoomSet.has(roomNo)
    );

    return {
      selectedCount,
      roomsWithoutDevicesCount: roomsWithoutDevices.length,
      roomsWithStandardRoomCount: roomsWithStandardRoom.length,
      placeableCount: placeableRoomNumbers.length,
      roomsWithoutDevices: roomsWithoutDevices, // valgfritt (kan være nyttig i neste steg)
      placeableRoomNumbers, // ✅ disse bruker du senere når du faktisk skal plassere
      roomsWithStandardRoom,
    };
  }
);
